import { useEffect, useMemo, useState } from "react";
import ModalComponent from "components/modal";
import { useSharedState } from "state";
import { useParams } from "react-router-dom";
import {
  addCommentToDocument,
  approveDocumentRequest,
  deleteDocument,
  getCommentsForDocument,
  getDocumentApprovals,
  getDocumentById,
  getDocumentFileLink,
  getDocumentFileNames,
  uploadDocumentFiles,
} from "api/document";
import moment from "moment";
import DangerModal from "components/modal/danger";
import WarningModal from "components/modal/warning";
import { StatusEnum } from "types/dict";
import { CommentProps } from "types/comment";
import getUserInitials from "helpers/get-user-initials";
import { FileDetails, FilesResponse } from "types/file";
import { IconDownload, IconEye, IconPlus } from "@tabler/icons-react";
import { DocumentProps } from "types/document";
import ForbiddenPage from "components/protected-route/forbidden";
import { PreviewFile } from "helpers/preview-file";

export interface ExpenseItem {
  name: string;
  quantity: number | undefined;
  price: number | undefined;
  sum: number | undefined;
  expense_item: string;
}

export const emptyExpenseRow: ExpenseItem = {
  name: "",
  quantity: undefined,
  price: undefined,
  sum: undefined,
  expense_item: "",
};

export interface StatusProps {
  name: string;
  position: string;
  status: number;
}

const deleteDocumentDanger = "Вы действительно хотите удалить данную задачу?";
const rejectDocument = "Вы действительно хотите отклонить данный договор?";
const approveDocument = "Вы действительно хотите утвердить данный договор?";

export default function DocumentDetail() {
  const { user, getDocumentsData, isAdmin, notify } = useSharedState();

  const { documentId } = useParams();

  const [documentItem, setDocumentItem] = useState<DocumentProps | undefined>(undefined);
  const [files, setFiles] = useState<FilesResponse | undefined>(undefined);

  const [isForbidden, setForbidden] = useState<boolean>(false);
  const [modalTab, setModalTab] = useState<"info" | "journal">("info");
  const [comments, setComments] = useState<CommentProps[] | undefined>(undefined);
  const [isFileLoading, setFileLoading] = useState<boolean>(false);

  const [newComment, setNewComment] = useState<string>("");
  const [isLeavingComment, setLeavingComment] = useState<boolean>(false);

  const [statuses, setStatuses] = useState<StatusProps[]>([]);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  useEffect(() => {
    initDocument();
  }, [documentId]);

  const initDocument = async () => {
    try {
      await getDocumentData();
      await getStatuses();
      await getFilesData();
      await getCommentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      if (error?.response?.status === 403) {
        notify(err, "danger", "Отказано в доступе");
        setForbidden(true);
      }
    }
  };

  const openDocumentModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const cancelCommentLeave = () => {
    setLeavingComment(false);
    setNewComment("");
  };

  const changeTab = (tab: "info" | "journal") => {
    setModalTab(tab);
    cancelCommentLeave();
  };

  const getDocumentData = async () => {
    try {
      const res: DocumentProps = await getDocumentById(Number(documentId));
      setDocumentItem(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении договора");
      throw error;
    }
  };

  const getStatuses = async () => {
    try {
      const approvals: StatusProps[] = await getDocumentApprovals(documentId ? +documentId : 0);
      setStatuses(approvals);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении статусов");
    }
  };

  const getFilesData = async () => {
    try {
      const res: FilesResponse = await getDocumentFileNames(Number(documentId));
      setFiles(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файлов");
    }
  };

  const getCommentsData = async () => {
    try {
      const res: any = await getCommentsForDocument(Number(documentId));
      setComments(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении комментариев");
    }
  };

  const uploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const formData = new FormData();

      if (e.target.files?.length) {
        Array.from(e.target.files).forEach((file) => {
          formData.append(`files`, file);
        });
      }

      await uploadDocumentFiles(Number(documentId), formData);

      notify("Файл добавлен", "success");
      getFilesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при добавлении файла");
    } finally {
      setFormLoading(false);
    }
  };

  const onFileAction = async (file: FileDetails, action: "preview" | "download") => {
    try {
      const res = await getDocumentFileLink(Number(documentId), file);
      PreviewFile(res, action);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении ссылки");
    } finally {
      setFileLoading(false);
    }
  };

  const sortedStatuses = useMemo(() => {
    const completedStatuses = [...statuses].filter((s) => s.status === 2);
    const rejectedStatuses = [...statuses].filter((s) => s.status === 3);
    const currentStatuses = [...statuses].filter((s) => s.status === 1);
    const nullStatuses = [...statuses].filter((s) => s.status === null);

    return [...completedStatuses, ...rejectedStatuses, ...currentStatuses, ...nullStatuses];
  }, [statuses]);

  const isDocumentActive = useMemo(() => {
    return documentItem && documentItem.status !== StatusEnum.COMPLETED && documentItem.status !== StatusEnum.REJECTED
      ? true
      : false;
  }, [documentItem]);

  const isDocumentDeleteable = useMemo(() => {
    return isDocumentActive && isAdmin ? true : false;
  }, [isDocumentActive, isAdmin]);

  const isDocumentApprovable = useMemo(() => {
    if (documentItem && documentItem.pending_approver_id === user.result.user_id && isDocumentActive) return true;
    return false;
  }, [documentItem, user, isDocumentActive]);

  const isFooterVisible = useMemo(() => {
    return isDocumentDeleteable || isDocumentApprovable;
  }, [isDocumentDeleteable, isDocumentApprovable]);

  const onDocumentDelete = async () => {
    openDocumentModal();
    setFormLoading(true);

    try {
      await deleteDocument(documentItem?.document_request_id || 0);

      setFormSubmitted(true);
      await getDocumentsData();
      notify("Договор удалён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении договора");
    } finally {
      setFormLoading(false);
    }
  };

  const onDocumentSubmit = async (approved: boolean) => {
    openDocumentModal();
    setFormLoading(true);

    try {
      await approveDocumentRequest(documentItem?.document_request_id || 0, approved);

      await initDocument();
      await getDocumentsData();
      notify(approved ? "Договор утверждён" : "Договор отклонён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", approved ? "Ошибка при утверждении договора" : "Ошибка при отклонении договора");
    } finally {
      setFormLoading(false);
    }
  };

  const onCommentLeave = async () => {
    setFormLoading(true);

    try {
      await addCommentToDocument(Number(documentId) || 0, newComment);

      cancelCommentLeave();
      await getCommentsData();
      notify("Комментарий сохранён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении комментария");
    } finally {
      setFormLoading(false);
    }
  };

  const detailsHeader = (
    <ul className="nav nav-tabs card-header-tabs nav-fill" data-bs-toggle="tabs" role="tablist">
      <li className="nav-item" role="presentation">
        <div
          className="nav-link active cursor-pointer"
          style={{ "--tblr-card-bg": "white" } as React.CSSProperties}
          data-bs-toggle="tab"
          aria-selected="true"
          role="tab"
          onClick={() => changeTab("info")}
        >
          Детали договора
        </div>
      </li>
      <li className="nav-item" role="presentation">
        <div
          className="nav-link cursor-pointer"
          style={{ "--tblr-card-bg": "white" } as React.CSSProperties}
          data-bs-toggle="tab"
          aria-selected="false"
          role="tab"
          onClick={() => changeTab("journal")}
        >
          Журнал
        </div>
      </li>
    </ul>
  );

  const detailsBody = (
    <div className="row">
      <div className="datagrid gap-3 col-sm-6 col-lg-8 mb-3">
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Контрагент</div>
          <div className="datagrid-content">{documentItem?.contragent || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Филиал</div>
          <div className="datagrid-content">{documentItem?.choosen_location || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Номер договора</div>
          <div className="datagrid-content">{documentItem?.lease_number || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Шаблон</div>
          <div className="datagrid-content">{documentItem?.template_name || "-"}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Период действия договора</div>
          <div className="datagrid-content">
            {moment(documentItem?.contract_start_date).format("DD.MM.YYYY") || "-"} -{" "}
            {moment(documentItem?.contract_end_date).format("DD.MM.YYYY") || "-"}
          </div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title text-primary">Создатель</div>
          <div className="datagrid-content">{documentItem?.creator_name || "-"}</div>
        </div>
        <div className="datagrid-item" style={{ gridColumn: "1 / -1" }}>
          <div className="datagrid-title text-primary">Информация по договору</div>
          <div className="datagrid-content">{documentItem?.description || "-"}</div>
        </div>
      </div>

      <div className="col-sm-12 col-lg-4 mb-3">
        <h3 className="datagrid-title text-primary mb-2">Статус заявки</h3>
        <div className="card-body">
          <ul
            className={`steps steps-counter steps-vertical ${
              documentItem?.status === StatusEnum.COMPLETED
                ? "steps-green"
                : documentItem?.status === StatusEnum.REJECTED
                ? "steps-red"
                : ""
            }`}
          >
            <li className="step-item step-purple">
              Заявка создана ({moment(documentItem?.request_date).format("DD.MM.YYYY HH:mm")})
            </li>

            {sortedStatuses.map((status, index) => (
              <li
                className={`step-item ${
                  status.status && index === statuses.filter((s) => s.status !== null).length - 1 ? "active" : ""
                }`}
                key={index}
              >
                {status.position}{" "}
                {status.status && index === statuses.filter((s) => s.status !== null).length - 1
                  ? `(${documentItem?.approver_name})`
                  : ""}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="col-lg-6 mb-3">
        <div className="datagrid-title d-flex align-items-center justify-content-between text-primary">
          Файлы
          {isFooterVisible && (
            <label htmlFor="new_file" className="btn btn-ghost-primary">
              <IconPlus className="icon" /> Добавить
              <input type="file" id="new_file" onChange={uploadFiles} multiple hidden />
            </label>
          )}
        </div>

        {files?.file_details.length ? (
          <div className="list-group">
            {files.file_details.map((file, index) => (
              <div key={index} className="list-group-item">
                <div className="row align-items-center">
                  <div className="col text-truncate">
                    <div className="d-block text-truncate mt-n1" title={file.file_name}>
                      {file.file_name}
                    </div>
                  </div>

                  <div className="col">
                    <div className="d-block text-truncate mt-n1">
                      {moment(file.saved_datetime).add(5, "hours").format("DD.MM.YY HH:mm") || "-"}
                    </div>
                  </div>

                  <div className="col-auto btn-list flex-nowrap">
                    <button
                      type="button"
                      title="Просмотреть"
                      className="btn btn-icon btn-ghost-primary d-flex gap-2"
                      onClick={() => onFileAction(file, "preview")}
                      disabled={isFileLoading}
                    >
                      {isFileLoading ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <IconEye className="icon" />
                      )}
                    </button>
                    <button
                      type="button"
                      title="Скачать"
                      className="btn btn-icon btn-ghost-primary d-flex gap-2"
                      onClick={() => onFileAction(file, "download")}
                      disabled={isFileLoading}
                    >
                      {isFileLoading ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <IconDownload className="icon" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>Нет файлов</div>
        )}
      </div>
    </div>
  );

  const journalBody = (
    <div className="row">
      <div className="col-lg-12">
        <ul className="timeline">
          {comments?.map((comment, index) => (
            <li key={index} className="timeline-event">
              <div className="timeline-event-icon bg-x-lt">{getUserInitials(comment.user_name)}</div>
              <div className="card timeline-event-card">
                <div className="card-body">
                  <div className="text-secondary float-end">
                    ({moment(comment.comment_date).format("DD.MM.YYYY HH:mm")})
                  </div>
                  <h4>{comment.user_name}</h4>
                  <p className="text-secondary">{comment.logs}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const detailsFooter = (
    <>
      {isDocumentDeleteable && (
        <button
          className="btn btn-ghost-danger d-flex gap-2"
          data-bs-toggle="modal"
          data-bs-target="#modal-delete-document"
          disabled={isFormLoading}
        >
          Удалить
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      {isDocumentApprovable && (
        <>
          <button
            className="btn btn-primary d-flex gap-2"
            data-bs-toggle="modal"
            data-bs-target="#modal-reject-document"
            disabled={isFormLoading}
          >
            Отклонить
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
          <button
            className="btn btn-success d-flex gap-2"
            data-bs-toggle="modal"
            data-bs-target="#modal-approve-document"
            disabled={isFormLoading}
          >
            Утвердить
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
        </>
      )}
    </>
  );

  const journalFooter = (
    <>
      {isLeavingComment ? (
        <>
          <input
            className="form-control"
            style={{ width: 0, flexGrow: 1 }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && newComment && !isFormLoading && onCommentLeave()}
            disabled={isFormLoading}
          />

          <button className="btn btn-ghost-primary d-flex gap-2" onClick={cancelCommentLeave} disabled={isFormLoading}>
            Отмена
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>

          <button
            className="btn btn-success d-flex gap-2"
            onClick={onCommentLeave}
            disabled={!newComment || isFormLoading}
          >
            Сохранить
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
        </>
      ) : (
        <button
          className="btn btn-success d-flex gap-2"
          onClick={() => setLeavingComment(true)}
          disabled={isFormLoading}
        >
          Добавить комментарий
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}
    </>
  );

  return (
    <>
      <ModalComponent
        header={detailsHeader}
        body={isForbidden ? <ForbiddenPage /> : modalTab === "info" ? detailsBody : journalBody}
        footer={
          isFooterVisible && modalTab === "info"
            ? detailsFooter
            : isFooterVisible && modalTab === "journal"
            ? journalFooter
            : undefined
        }
        alignHeaderToBottom
        disableClose={isFormLoading}
        shouldModalClose={isFormSubmitted}
        shouldModalNavigate={"/documents"}
      />
      <DangerModal
        modal_id="modal-delete-document"
        dangerDescription={deleteDocumentDanger}
        cancel={openDocumentModal}
        confirm={onDocumentDelete}
      />
      <WarningModal
        modal_id="modal-reject-document"
        warningDescription={rejectDocument}
        cancel={openDocumentModal}
        confirm={() => onDocumentSubmit(false)}
      />
      <WarningModal
        modal_id="modal-approve-document"
        warningDescription={approveDocument}
        cancel={openDocumentModal}
        confirm={() => onDocumentSubmit(true)}
      />
    </>
  );
}
